import {
  FlexBox,
  HtmlButtonProps,
  Icon,
  IconButton,
  SelectionList,
  TextWithHighlight,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { memo, useCallback, useState } from 'react';

import {
  EnhancedTemplate,
  useQuickReplyData,
} from 'components/editorQuickRepliesDropdown/components/quickRepliesContent/util/useQuickReplyData';

import styles from './templateItem.module.scss';

interface TemplateListProps {
  item: EnhancedTemplate;
  toggleFavorite: ReturnType<typeof useQuickReplyData>['toggleFavorite'];
  searchTerm?: string;
  locale: string;

  showAutomation?: boolean;
}

export const TemplateItem = memo(
  ({
    item,
    toggleFavorite,
    searchTerm,
    locale,
    showAutomation = false,
  }: TemplateListProps): JSX.Element => {
    const [isHovered, setIsHovered] = useState<boolean>(false);

    const handleOnClick: HtmlButtonProps['onClick'] = useCallback(
      (e) => {
        e.stopPropagation();
        toggleFavorite(item, locale);
      },
      [item, locale, toggleFavorite],
    );

    return (
      <SelectionList.Item
        key={item.id}
        value={item}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        className={classNames({
          [styles.item]: true,
          [styles.itemHovered]: isHovered,
          [styles.itemFavorite]: item.favorite,
        })}
        noFixedSelectionStateSpace={showAutomation}
      >
        <FlexBox
          key={item.id}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <FlexBox
            alignItems="center"
            gap="200"
            className={styles.textContainer}
          >
            {showAutomation && (
              <FlexBox margin="0 0 -2px">
                {(item.automation_id && (
                  <Tooltip content="Using this template will also trigger an associated automation.">
                    <Icon name="flow" className={styles.automation} />
                  </Tooltip>
                )) || <FlexBox width="16px" height="16px" />}
              </FlexBox>
            )}
            {!searchTerm && (
              <span className={styles.templateName}>{item.name}</span>
            )}
            {searchTerm && (
              <TextWithHighlight
                highlight={searchTerm}
                text={item.name}
                className={styles.templateName}
              />
            )}
          </FlexBox>

          <div className={styles.iconButtons}>
            <IconButton
              icon={item.favorite ? 'star-full' : 'star'}
              variant="tertiary"
              iconSize={16}
              onClick={handleOnClick}
              className={styles.favoriteButton}
              tabIndex={-1}
            />
          </div>
        </FlexBox>
      </SelectionList.Item>
    );
  },
);

TemplateItem.displayName = 'TemplateItem';
